import { inject } from "../../di";
import type {
  BroadcasterTelemetrySnapshot,
  IWritableBroadcasterDiagnosticsState,
  IBroadcasterDiagnosticsService,
  QualityRating,
  WarningInfo,
  WarningSeverity,
} from "./types";

interface WarningCondition {
  key: string;
  message: string;
  severity: WarningSeverity;
  debounceSeconds: number;
  check: (snapshot: BroadcasterTelemetrySnapshot) => boolean;
}

const WARNING_CONDITIONS: WarningCondition[] = [
  {
    key: "bandwidth",
    message: "Bandwidth limited",
    severity: "moderate",
    debounceSeconds: 5,
    check: (s) => s.qualityLimitationReason === "bandwidth",
  },
  {
    key: "cpu",
    message: "CPU limited",
    severity: "moderate",
    debounceSeconds: 5,
    check: (s) => s.qualityLimitationReason === "cpu",
  },
  {
    key: "highRtt",
    message: "High latency",
    severity: "moderate",
    debounceSeconds: 5,
    check: (s) => s.currentRtt > 200,
  },
  {
    key: "lowBitrate",
    message: "Very low bitrate",
    severity: "critical",
    debounceSeconds: 5,
    check: (s) => s.videoBitrateSent < 1500,
  },
  {
    key: "lowFps",
    message: "Low frame rate",
    severity: "moderate",
    debounceSeconds: 5,
    check: (s) => s.framesPerSecond < 15,
  },
  {
    key: "packetLoss",
    message: "Packet loss detected",
    severity: "moderate",
    debounceSeconds: 5,
    check: (s) => s.remoteFractionLost > 0.03,
  },
  {
    key: "iceDisconnected",
    message: "Connection interrupted",
    severity: "critical",
    debounceSeconds: 0,
    check: (s) => s.iceConnectionState === "disconnected",
  },
  {
    key: "iceFailed",
    message: "Connection failed",
    severity: "critical",
    debounceSeconds: 0,
    check: (s) => s.iceConnectionState === "failed",
  },
];

export class BroadcasterDiagnosticsService implements IBroadcasterDiagnosticsService {
  @inject<IWritableBroadcasterDiagnosticsState>("writableBroadcasterDiagnosticsState")
  private state!: IWritableBroadcasterDiagnosticsState;

  private snapshotCount = 0;
  private warningCounters = new Map<string, number>();
  private lastToastTime = 0;
  private previousRating: QualityRating | null = null;
  private toastHandler?: (type: "warning" | "success", message: string) => void;

  initialize(): void {}

  dispose(): void {}

  feedSnapshot(snapshot: BroadcasterTelemetrySnapshot): void {
    this.handleSnapshot(snapshot);
  }

  reset(): void {
    this.snapshotCount = 0;
    this.warningCounters.clear();
    this.lastToastTime = 0;
    this.previousRating = null;
    this.state.qualityScore.set(0);
    this.state.qualityRating.set("excellent");
    this.state.isWarmingUp.set(true);
    this.state.activeWarning.set(null);
    this.state.latestSnapshot.set(null);
    this.state.showDebugOverlay.set(false);
  }

  toggleDebugOverlay(): void {
    this.state.showDebugOverlay.set(!this.state.showDebugOverlay.get());
  }

  shouldBlockGoLive(): boolean {
    const rating = this.state.qualityRating.get();
    return rating === "poor" || rating === "critical";
  }

  setToastHandler(handler: (type: "warning" | "success", message: string) => void): void {
    this.toastHandler = handler;
  }

  private handleSnapshot(snapshot: BroadcasterTelemetrySnapshot): void {
    this.snapshotCount++;
    this.state.latestSnapshot.set(snapshot);
    this.state.qualityScore.set(snapshot.qualityScore);

    const rating = this.scoreToRating(snapshot.qualityScore);
    this.state.qualityRating.set(rating);

    if (this.snapshotCount >= 5) {
      this.state.isWarmingUp.set(false);
    }

    this.evaluateWarnings(snapshot);
    this.evaluateTransitionToasts(rating);

    this.previousRating = rating;
  }

  private scoreToRating(score: number): QualityRating {
    if (score >= 80) return "excellent";
    if (score >= 60) return "good";
    if (score >= 40) return "moderate";
    if (score >= 20) return "poor";
    return "critical";
  }

  private evaluateWarnings(snapshot: BroadcasterTelemetrySnapshot): void {
    let highestWarning: WarningInfo | null = null;

    for (const condition of WARNING_CONDITIONS) {
      const isActive = condition.check(snapshot);

      if (isActive) {
        const count = (this.warningCounters.get(condition.key) ?? 0) + 1;
        this.warningCounters.set(condition.key, count);

        if (count >= condition.debounceSeconds) {
          if (!highestWarning || this.severityRank(condition.severity) > this.severityRank(highestWarning.severity)) {
            highestWarning = { message: condition.message, severity: condition.severity };
          }
        }
      } else {
        this.warningCounters.set(condition.key, 0);
      }
    }

    this.state.activeWarning.set(highestWarning);
  }

  private severityRank(severity: WarningSeverity): number {
    return severity === "critical" ? 2 : 1;
  }

  private evaluateTransitionToasts(rating: QualityRating): void {
    if (!this.previousRating || this.state.isWarmingUp.get()) return;

    const now = Date.now();
    if (now - this.lastToastTime < 30_000) return;

    const prevRank = this.ratingRank(this.previousRating);
    const currRank = this.ratingRank(rating);

    if (currRank <= 1 && prevRank > 1) {
      this.showToast("warning", `Stream quality degraded to ${rating}`);
    } else if (currRank >= 3 && prevRank < 3) {
      this.showToast("success", "Stream quality recovered");
    }
  }

  private ratingRank(rating: QualityRating): number {
    const ranks: Record<QualityRating, number> = { critical: 0, poor: 1, moderate: 2, good: 3, excellent: 4 };
    return ranks[rating];
  }

  private showToast(type: "warning" | "success", message: string): void {
    this.lastToastTime = Date.now();
    this.toastHandler?.(type, message);
  }
}
